/**
 * 사용설명서 검색 — 순수부.
 *
 * ⚠ import를 넣지 말 것. scripts/test-help-search.js가 이 파일을 transpileModule로 통째로 평가해
 * node에서 검증한다(src/utils/messageTable.ts와 같은 관례). DOM을 다루는 쪽은 useHelpSearch.ts다.
 *
 * 매칭 규칙: 띄어쓰기·대소문자 무시("예약전송"으로 "예약 전송"을 찾는다), 여러 낱말은 구절 우선 →
 * 없으면 AND(searchPlans — AND 판정은 블록 단위라 이 파일은 낱말별 발견 여부만 돌려준다),
 * 사용자 말 → 설명서 말 동의어 확장.
 */

/**
 * 사용자가 흔히 쓰는 말 → 설명서에 실제로 적힌 말. 원래 낱말도 함께 찾는다.
 * 키는 낱말 하나와 정확히 일치할 때만 확장된다(부분 일치 아님). 키·값 모두 정규화 후 비교하므로
 * 띄어쓰기는 상관없다. 값이 본문(sections/*.tsx)에 실제로 있는지는 test-help-search.js가 확인한다.
 */
export const SEARCH_ALIASES: Record<string, string[]> = {
  다운로드: ['내려받'],
  다운: ['내려받'],
  화상통화: ['영상 통화'],
  화상: ['영상'],
  이모티콘: ['이모지'],
  캡쳐: ['캡처'],
  비번: ['비밀번호'],
  핸드폰: ['휴대폰'],
  스마트폰: ['휴대폰'],
  모바일: ['휴대폰'],
  카톡: ['카카오톡'],
  단톡: ['그룹'],
  단톡방: ['그룹'],
  음소거: ['알림 끄기'],
  알림끄기: ['음소거'],
  사진: ['이미지'],
  이미지: ['사진'],
  공지사항: ['공지'],
  일대일: ['1:1'],
  '1대1': ['1:1'],
  업로드: ['첨부'],
  달력: ['캘린더'],
  스케줄: ['일정'],
  약속: ['일정'],
};

/** 결과가 없을 때 띄우는 추천 낱말 — 각각 결과가 1곳 이상 나와야 한다 */
export const SEARCH_SUGGESTIONS = ['알림', '첨부', '예약 전송', '자동 로그인', '화면 캡처', '업데이트'];

const INVISIBLE = /[​-‍﻿]/;

/**
 * 공백(NBSP 포함)·zero-width를 지우고 글자 단위로 소문자화한 문자열과,
 * 정규화 문자열의 i번째 글자가 원문의 몇 번째 글자였는지(`map[i]`)를 함께 돌려준다.
 * 소문자화로 길이가 바뀌는 문자(예: 'İ')는 원문 그대로 둔다 — 그래야 map이 1:1로 유지된다.
 */
export function normalizeWithMap(raw: string): { norm: string; map: number[] } {
  let norm = '';
  const map: number[] = [];
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    if (/\s/.test(c) || INVISIBLE.test(c)) continue;
    const lower = c.toLowerCase();
    norm += lower.length === 1 ? lower : c;
    map.push(i);
  }
  return { norm, map };
}

const normalizeTerm = (s: string) => normalizeWithMap(s).norm;

let aliasIndex: Record<string, string[]> | null = null;
function getAliasIndex(): Record<string, string[]> {
  if (aliasIndex) return aliasIndex;
  const index: Record<string, string[]> = {};
  Object.keys(SEARCH_ALIASES).forEach((key) => {
    index[normalizeTerm(key)] = SEARCH_ALIASES[key].map(normalizeTerm).filter(Boolean);
  });
  aliasIndex = index;
  return index;
}

/**
 * 검색어 → 낱말 목록. 낱말마다 [원래 낱말, ...동의어] 변형 배열이고 이미 정규화돼 있다.
 * 중복 낱말은 하나로 접는다. 빈 검색어는 [].
 */
export function parseQuery(query: string): string[][] {
  const aliases = getAliasIndex();
  const seen = new Set<string>();
  const terms: string[][] = [];
  query
    .trim()
    .split(/\s+/)
    .forEach((word) => {
      const term = normalizeTerm(word);
      if (!term || seen.has(term)) return;
      seen.add(term);
      const variants = [term];
      (aliases[term] || []).forEach((alias) => {
        if (!variants.includes(alias)) variants.push(alias);
      });
      terms.push(variants);
    });
  return terms;
}

/**
 * 찾는 순서. 낱말이 둘 이상이면 ① 붙여 쓴 구절 하나("자동 로그인" → "자동로그인")로 먼저 찾고,
 * 결과가 없을 때만 ② 낱말별 AND로 넓힌다. ②만 쓰면 '자동'과 '로그인'이 따로 들어간 긴 문단이
 * 전부 걸려 결과가 수십 곳이 되고 강조가 흩어진다. 첫 계획부터 차례로 시도해 결과가 나온 것을 쓴다.
 */
export function searchPlans(query: string): string[][][] {
  const words = parseQuery(query);
  if (words.length < 2) return words.length ? [words] : [];
  // 붙인 구절도 parseQuery를 거쳐 동의어를 받는다("화상 통화" → "화상통화" → "영상통화")
  return [parseQuery(words.map((variants) => variants[0]).join('')), words];
}

/**
 * 정규화 문자열에서 모든 낱말의 모든 변형을 찾아 **원문 기준** [start, end) 구간으로 돌려준다.
 * 구간은 시작순 정렬·겹치면 병합(비중첩). `matched[i]`는 i번째 낱말이 하나라도 발견됐는지 —
 * AND 판정은 여러 문단을 합친 블록 단위라 호출부가 OR로 누적한다.
 * 띄어쓰기를 건너뛰고 찾았으면 구간이 그 공백까지 덮는다("예약전송" → 원문 "예약 전송" 전체).
 */
export function findSpans(
  norm: string,
  map: number[],
  terms: string[][]
): { spans: Array<[number, number]>; matched: boolean[] } {
  const found: Array<[number, number]> = [];
  const matched = terms.map(() => false);
  terms.forEach((variants, ti) => {
    variants.forEach((v) => {
      if (!v) return;
      let from = 0;
      for (;;) {
        const at = norm.indexOf(v, from);
        if (at < 0) break;
        matched[ti] = true;
        found.push([map[at], map[at + v.length - 1] + 1]);
        from = at + v.length;
      }
    });
  });
  found.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const spans: Array<[number, number]> = [];
  found.forEach(([s, e]) => {
    const last = spans[spans.length - 1];
    if (last && s < last[1]) {
      if (e > last[1]) last[1] = e;
    } else {
      spans.push([s, e]);
    }
  });
  return { spans, matched };
}

/**
 * 텍스트 노드 여럿을 이어 붙인 문자열의 오프셋 → [노드 인덱스, 노드 안 오프셋].
 * `starts[k]`는 k번째 노드가 이어 붙인 문자열에서 시작하는 위치(빈 노드는 넣지 않는다).
 * 노드 경계에 걸친 오프셋은 구간 시작이면 뒤 노드의 0, 구간 끝(`isEnd`)이면 앞 노드의 끝으로 둔다 —
 * 그래야 Range가 옆 노드를 0글자만큼 물고 들어가지 않는다.
 */
export function locateOffset(starts: number[], offset: number, isEnd: boolean): [number, number] {
  let lo = 0;
  let hi = starts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (starts[mid] <= offset) lo = mid;
    else hi = mid - 1;
  }
  const k = isEnd && lo > 0 && starts[lo] === offset ? lo - 1 : lo;
  return [k, offset - starts[k]];
}

/** 결과 목록용 한 줄: 매치 앞 `before`자·뒤 `after`자, 공백은 하나로 접고 잘린 쪽엔 …를 붙인다 */
export function makeSnippet(raw: string, start: number, end: number, before = 24, after = 48): string {
  const from = Math.max(0, start - before);
  const to = Math.min(raw.length, end + after);
  const body = raw.slice(from, to).replace(/\s+/g, ' ').trim();
  return (from > 0 ? '…' : '') + body + (to < raw.length ? '…' : '');
}

/** 결과 목록의 제목·스니펫을 React로 칠할 조각. 강조 위치는 본문과 같은 규칙(findSpans)으로 정한다 */
export function highlightSegments(text: string, terms: string[][]): Array<{ text: string; hit: boolean }> {
  if (!terms.length) return [{ text, hit: false }];
  const { norm, map } = normalizeWithMap(text);
  const { spans } = findSpans(norm, map, terms);
  const out: Array<{ text: string; hit: boolean }> = [];
  let pos = 0;
  spans.forEach(([s, e]) => {
    if (s > pos) out.push({ text: text.slice(pos, s), hit: false });
    out.push({ text: text.slice(s, e), hit: true });
    pos = e;
  });
  if (pos < text.length) out.push({ text: text.slice(pos), hit: false });
  return out;
}
